import wrapperHOC from "@Utils/wrapperHOC";
import { memo, useCallback, useEffect, useState } from "react";
import Carousel from "@Components/Carousel";
import Image from "@Components/Image";
import Skeleton from "@Components/Skeleton";
import styles from "./BlogPartnersCarousel.module.scss";

interface BlogPartner {
  id: string;
  image: {
    id: number;
    alt: string;
    url: string;
    filename?: string;
    mimeType?: string;
    filesize?: number;
    width?: number;
    height?: number;
  };
  link: string;
}

interface BlogPartnersCarouselProps {
  partners: BlogPartner[];
}

const BlogPartnersCarousel = ({ partners }: BlogPartnersCarouselProps) => {
  const [isMounted, setIsMounted] = useState(false);
  const [activeSlide, setActiveSlide] = useState(1);
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const handleImageLoad = (partnerId: string) => {
    setLoadedImages((prev) => new Set(prev).add(partnerId));
  };

  const handlePartnerClick = useCallback((link: string) => {
    window.open(link, "_blank", "noopener,noreferrer");
  }, []);

  const renderSlide = (partner: BlogPartner, index: number) => {
    const partnerId = partner.id || `partner-${index}`;
    const isLoaded = loadedImages.has(partnerId);

    return (
      <div
        role="button"
        tabIndex={0}
        className={styles.partnerLink}
        key={partnerId}
        onClick={() => handlePartnerClick(partner.link)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handlePartnerClick(partner.link);
          }
        }}
        aria-label={partner.image.alt || "Blog Partner"}
      >
        <div className={styles.partnerImage}>
          <Skeleton width="100%" height="100%" addBorderRadius showSkeleton={!isLoaded}>
            <Image
              src={`${partner.image.url}?w=500`}
              alt={partner.image.alt || "Blog Partner"}
              width="100%"
              height="auto"
              onLoad={() => handleImageLoad(partnerId)}
              style={{ opacity: isLoaded ? 1 : 0, transition: "opacity 0.3s ease" }}
            />
          </Skeleton>
        </div>
      </div>
    );
  };

  if (!isMounted) {
    return null;
  }

  const slideList = partners.map((partner, index) => renderSlide(partner, index));

  return (
    <div className={styles.carouselContainer}>
      <Carousel
        slideList={slideList}
        activePos={activeSlide}
        handleChangeSlide={setActiveSlide}
        autoSlide
        delay={10}
        showNavSlider={partners.length > 1}
        className={styles.carousel}
        sliderContainerClassName={styles.sliderContainer}
      />
    </div>
  );
};

export default wrapperHOC(memo(BlogPartnersCarousel), {
  componentName: "BlogPartnersCarousel-Blog",
  showForChina: true,
});
